import { Injectable, Injector } from '@angular/core';

import { HttpDataService } from './http-data.service';
import { Itinerary } from 'core/models/itinerary';
import { lastValueFrom } from 'rxjs';

@Injectable()
export class ItinerariesService extends HttpDataService {
  constructor(protected injector: Injector) {
    super(injector, '/courses');
    // super(injector, '/catalogues/itineraries');
  }

  /*
   * The query param removable has been set to false because it causes a significant delay
   * See https://gamelearn.atlassian.net/browse/LMS-4148
   * When we apply pagination try to set removable to true again
   */
  getAccountItineraries(lang?: string, meta = false, archived = false, removable = false) {
    const metaQuery = meta ? `&meta=true&lang=${lang}` : '';
    const archivedQuery = archived ? `&archived=true` : '';
    const removableQuery = removable ? '&removable=true' : '';
    const query = `?lang=${lang}${metaQuery}${archivedQuery}${removableQuery}`;
    return lastValueFrom(this.http.get(`${this.baseUrl}/itineraries/account?${query}`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  getAccountItinerariesByClientId(clientId: string, lang?: string) {
    return lastValueFrom(
      this.http.get(
        `${this.baseUrl}/catalogues/clients/${clientId}?meta=true${
          lang ? '&lang=' + lang : ''
        }&archived=true&itineraries=true`
      )
    )
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  getItinerariesByCourse(courseId: string, lang: string) {
    return lastValueFrom(this.http.get(`${this.baseUrl}/itineraries/courses/${courseId}?lang=${lang}`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error)) as Promise<Array<any>>;
  }

  getItinerary(itineraryId: string, clientId?: string, lang?: string) {
    return lastValueFrom(
      this.http.get(`${this.baseUrl}/itineraries/${itineraryId}?lang=${lang}${clientId ? '&clientId=' + clientId : ''}`)
    )
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  createItinerary(data: any, lang?: string) {
    return lastValueFrom(this.http.post(`${this.baseUrl}/itineraries?lang=${lang}`, data))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  updateItinerary(itinerary: any, clientId?: string): Promise<Itinerary | any> {
    return lastValueFrom(
      this.http.put(`${this.baseUrl}/itineraries/${itinerary.id}${clientId ? '?clientId=' + clientId : ''}`, itinerary)
    )
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  deleteItinerary(itineraryId: string) {
    return lastValueFrom(this.http.delete(`${this.baseUrl}/itineraries/${itineraryId}`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  updateItineraryVisibility(catalogueId: string, accessChangeConfirmed: boolean, lang: string, data: any) {
    return lastValueFrom(
      this.http.post(
        `${this.baseUrl}/catalogues/${catalogueId}/item?accessChangeConfirmed=${accessChangeConfirmed}${
          lang ? '&lang=' + lang : ''
        }`,
        data
      )
    )
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  updateItinerariesBulk(data) {
    return lastValueFrom(this.http.put(`${this.baseUrl}/itineraries/bulk`, data))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  getItineraryTranslations(itineraryId: string) {
    return lastValueFrom(this.http.get(`${this.baseUrl}/itineraries/${itineraryId}/lang`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  createItineraryTranslation(data: any, itineraryId: string) {
    return lastValueFrom(this.http.post(`${this.baseUrl}/itineraries/${itineraryId}/lang`, data))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  updateItineraryTranslation(data: any, itineraryId: string) {
    return lastValueFrom(this.http.put(`${this.baseUrl}/itineraries/${itineraryId}/lang`, data))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  deleteItineraryTranslation(itineraryId: string, lang?: string) {
    return lastValueFrom(this.http.delete(`${this.baseUrl}/itineraries/${itineraryId}/lang/${lang}`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  publishItinerary(data: any) {
    return lastValueFrom(this.http.put(`${this.baseUrl}/itineraries/${data.id}/publish`, data))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }
}
